/**
 * @fileoverview Date and time utilities for database and display formatting
 *
 * This module provides utilities for converting between:
 * - Database timestamps (PostgreSQL/MySQL)
 * - Display formats (US Pacific timezone by default)
 * - ISO strings
 * - Different timezone representations
 *
 * All functions handle both date-only and full datetime values appropriately.
 * Default timezone is America/Los_Angeles (PT) unless specified.
 *
 * @see {@link src/types/database.ts} Database type definitions
 * @see {@link src/utils/validation.ts} Date validation utilities
 * @see {@link src/utils/formatNumber.ts} Format number for currencies and non-financial numbers
 */

interface DateTimeOptions {
  /** Target timezone (defaults to America/Los_Angeles) */
  toTimezone?: string;
  /** Locale for formatting (defaults to en-US) */
  locale?: string;
}

/** Options for date-only formatting */
interface DateFormatOptions {
  /** Locale for formatting (defaults to en-US) */
  locale?: string;
}

/** Display format options for dates/times */
export const formatValues = {
  /** Jan 1, 2023 */
  short: "short",
  /** January 1, 2023 */
  long: "long",
  /** 01/01/2023 */
  numeric: "numeric",
  /** 2023-01-01 */
  iso: "iso",
  /** Jan 1, 2023 2:15 PM PT */
  shortDt: "shortDt",
  /** January 1, 2023 14:15:00 PST */
  longDt: "longDt",
} as const;

export const DateFormat = {
  /** Jan 1, 2023 */
  shortMonthDayYear: formatValues.short,
  /** January 1, 2023 */
  longMonthDayYear: formatValues.long,
  /** 01/01/2023 */
  numericMdy: formatValues.numeric,
  /** 2023-01-01 */
  isoDate: formatValues.iso,
  /** Jan 1, 2023 2:15 PM PT */
  shortDatetime: formatValues.shortDt,
  /** January 1, 2023 14:15:00 PST */
  longDatetime: formatValues.longDt,
} as const;

// Export types
export type { DateTimeOptions };
export type DateFormatType = (typeof formatValues)[keyof typeof formatValues];

// Core Database <-> Display conversions
/**
 * Formats a database timestamp for display in the UI
 * Handles both date-only and datetime fields from the database
 *
 * @param dbTimestamp - Raw timestamp from database (UTC)
 * @param format - Desired display format
 * @param options - Formatting options (timezone, locale)
 * @returns Formatted date/time string in specified format and timezone
 */
export function formatDateTimeFromDB(
  dbTimestamp: string,
  format: DateFormatType,
  options?: DateTimeOptions
): string {
  if (!dbTimestamp) return "—";

  try {
    const date = new Date(dbTimestamp);
    const targetTimezone = options?.toTimezone || "America/Los_Angeles";
    const locale = options?.locale || "en-US";

    switch (format) {
      case "short":
        return date.toLocaleDateString(locale, {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: targetTimezone,
        });
      case "long":
        return date.toLocaleDateString(locale, {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: targetTimezone,
        });
      case "numeric":
        return date.toLocaleDateString(locale, {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          timeZone: targetTimezone,
        });
      case "iso":
        return date.toISOString().split("T")[0];
      case "shortDt":
        return date.toLocaleString(locale, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
          timeZone: targetTimezone,
        });
      case "longDt":
        return date.toLocaleString(locale, {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
          timeZone: targetTimezone,
        });
      default:
        return date.toISOString();
    }
  } catch (error) {
    console.error("Error formatting date from DB:", error);
    return "—";
  }
}

/**
 * Converts a display date/time to database format (UTC timestamp)
 *
 * @param displayDateTime - Date/time in display format
 * @returns UTC timestamp string for database storage
 */
export function formatDateTimeToDB(displayDateTime: string): string {
  if (!displayDateTime) return "";

  try {
    const date = new Date(displayDateTime);
    return date.toISOString();
  } catch (error) {
    console.error("Error formatting date to DB:", error);
    return "";
  }
}

/**
 * Formats a database date (without time) for display
 *
 * @param dbDate - Date from database (YYYY-MM-DD)
 * @param format - Desired display format
 * @param options - Formatting options (locale)
 * @returns Formatted date string
 */
export function formatDateFromDB(
  dbDate: string,
  format: DateFormatType,
  options?: DateFormatOptions
): string {
  if (!dbDate) return "—";

  try {
    const date = new Date(dbDate);
    const locale = options?.locale || "en-US";

    switch (format) {
      case "short":
        return date.toLocaleDateString(locale, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      case "long":
        return date.toLocaleDateString(locale, {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      case "numeric":
        return date.toLocaleDateString(locale, {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });
      case "iso":
        return date.toISOString().split("T")[0];
      default:
        return date.toLocaleDateString(locale);
    }
  } catch (error) {
    console.error("Error formatting date from DB:", error);
    return "—";
  }
}

/**
 * Converts a display date to database format (YYYY-MM-DD)
 *
 * @param displayDate - Date in display format
 * @returns YYYY-MM-DD string for database storage
 */
export function formatDateToDB(displayDate: string): string {
  if (!displayDate) return "";

  try {
    const date = new Date(displayDate);
    return date.toISOString().split("T")[0];
  } catch (error) {
    console.error("Error formatting date to DB:", error);
    return "";
  }
}

/**
 * Converts a datetime between any two timezones
 *
 * @param datetime - Date/time string or Date object
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @param format - Format to return the result in
 * @returns Converted date/time in target timezone
 */
export function convertBetweenTimezones(
  datetime: string | Date,
  fromTimezone: string,
  toTimezone: string,
  format: DateFormatType
): string {
  if (!datetime) return "—";

  try {
    const date = typeof datetime === "string" ? new Date(datetime) : datetime;
    return formatDateTimeFromDB(date.toISOString(), format, {
      toTimezone,
    });
  } catch (error) {
    console.error("Error converting between timezones:", error);
    return "—";
  }
}

/**
 * Parses a display date/time string into a Date object
 *
 * @param displayDateTime - Date/time string in known format
 * @returns Date object
 */
export function parseDisplayDateTime(displayDateTime: string): Date {
  if (!displayDateTime) {
    throw new Error("Invalid date string provided");
  }

  try {
    return new Date(displayDateTime);
  } catch (error) {
    console.error("Error parsing display date/time:", error);
    throw error;
  }
}

/**
 * Validates if a string is a valid date/time
 *
 * @param dateTimeString - String to validate
 * @returns true if valid, false otherwise
 */
export function isValidDateTime(dateTimeString: string): boolean {
  if (!dateTimeString) return false;

  try {
    const date = new Date(dateTimeString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

// Backward compatibility functions
/**
 * @deprecated Use formatDateTimeFromDB with DateFormat.shortMonthDayYear instead
 */
export function convertDateMonDDYYYY(
  date: string | Date | null | undefined
): string {
  if (!date) return "—";
  return formatDateTimeFromDB(
    date instanceof Date ? date.toISOString() : String(date),
    "short"
  );
}

/**
 * @deprecated Use formatDateTimeFromDB with DateFormat.longMonthDayYear instead
 */
export function convertDateMonthDDYYYY(
  date: string | Date | null | undefined
): string {
  if (!date) return "—";
  return formatDateTimeFromDB(
    date instanceof Date ? date.toISOString() : String(date),
    "long"
  );
}

/**
 * @deprecated Use formatDateTimeFromDB with DateFormat.numericMdy instead
 */
export function convertDateMon2DigitDDYYYY(
  date: string | Date | null | undefined
): string {
  if (!date) return "—";
  return formatDateTimeFromDB(
    date instanceof Date ? date.toISOString() : String(date),
    "numeric"
  );
}

/**
 * @deprecated Use formatDateToDB instead
 */
export function convertDateYYYYMMDD(
  date: string | Date | null | undefined
): string {
  if (!date) return "—";
  return formatDateToDB(
    date instanceof Date ? date.toISOString() : String(date)
  );
}
